import { ArrowLeftOutlined,CloseOutlined,DownloadOutlined,PrinterOutlined } from "@ant-design/icons";
import { Button,Flex,Grid,Tooltip,Typography,theme } from "../../../../../shared/frontend/ui";
import React from "react";
import { LoadingOverlay } from "./LoadingOverlay";
import { windowHeight } from "../../../../../shared/frontend/theme/displayZoom";
import { KEY_ESCAPE } from "../../../../../shared/frontend/ui/keyboardKeys";

const CONTENT_MIN_HEIGHT_MOBILE_PX = 360;
const CONTENT_MIN_HEIGHT_DESKTOP_PX = 520;

type ListDocumentInlayProps = {
  title: React.ReactNode;
  text: (key: string) => string;
  children: React.ReactNode;
  extra?: React.ReactNode;
  onBack: () => void;
  onClose?: () => void;
  onPrint?: () => void;
  printDisabled?: boolean;
  onDownload?: () => void;
  downloadDisabled?: boolean;
  contentLoading?: boolean;
  downloadPrimary?: boolean;
  closeOnEscape?: boolean;
  contentHeight?: number | string;
  flushContent?: boolean;
  /** `auto` wenn über der Datei noch Text steht (Mail, Mahnung); sonst füllt die Vorschau die Fläche. */
  contentOverflow?: "hidden" | "auto";
};

export function ListDocumentInlay({
  title,
  text,
  children,
  extra,
  onBack,
  onClose,
  onPrint,
  printDisabled = false,
  onDownload,
  downloadDisabled = false,
  contentLoading,
  downloadPrimary = true,
  closeOnEscape = true,
  contentHeight = `min(${windowHeight("76vh")}, 860px)`,
  flushContent = false,
  contentOverflow = "hidden"
}: ListDocumentInlayProps) {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const resolvedContentHeight = isMobile ? `min(${windowHeight("64dvh")}, 620px)` : contentHeight;
  const resolvedContentMinHeight = isMobile ? CONTENT_MIN_HEIGHT_MOBILE_PX : CONTENT_MIN_HEIGHT_DESKTOP_PX;
  const content = (
    <div
      style={{
        background: token.colorBgLayout,
        border: flushContent ? "none" : `1px solid ${token.colorBorderSecondary}`,
        borderRadius: flushContent ? 0 : 8,
        height: resolvedContentHeight,
        minHeight: resolvedContentMinHeight,
        overflow: contentOverflow
      }}
    >
      {children}
    </div>
  );

  React.useEffect(() => {
    if (!closeOnEscape) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== KEY_ESCAPE || event.isComposing || event.defaultPrevented) return;

      event.preventDefault();
      onBack();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeOnEscape, onBack]);

  return (
    <div
      style={{
        margin: flushContent ? 0 : "-8px 0",
        padding: flushContent ? 0 : isMobile ? "8px 8px 12px" : "12px 12px 16px",
        background: flushContent ? token.colorBgContainer : token.colorFillQuaternary,
        borderTop: flushContent ? "none" : `1px solid ${token.colorBorderSecondary}`,
        borderBottom: flushContent ? "none" : `1px solid ${token.colorBorderSecondary}`,
        overflow: "hidden"
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        wrap
        style={{
          width: "100%",
          marginBottom: flushContent ? 0 : isMobile ? 8 : 12,
          padding: flushContent ? (isMobile ? "10px 12px" : "12px 16px") : 0,
          borderBottom: flushContent ? `1px solid ${token.colorBorderSecondary}` : "none",
          background: token.colorBgContainer
        }}
      >
        <Flex
          align="center"
          gap={isMobile ? 6 : 12}
          style={{ minWidth: 0, flex: isMobile ? "1 1 180px" : "1 1 260px" }}
        >
          <Tooltip title={isMobile ? text("Back to list") : undefined}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} aria-label={text("Back to list")}>
              {isMobile ? null : text("Back to list")}
            </Button>
          </Tooltip>
          <Typography.Title level={5} style={{ margin: 0, minWidth: 0, flex: "1 1 auto", overflowWrap: "anywhere" }}>
            {title}
          </Typography.Title>
        </Flex>
        <Flex
          className="list-document-inlay-actions"
          wrap="wrap"
          gap={8}
          justify="flex-end"
          align="center"
          style={{ marginLeft: "auto", flex: "0 0 auto", minWidth: 0 }}
        >
          {extra}
          {onPrint ? (
            <Tooltip title={isMobile ? text("Print") : undefined}>
              <Button icon={<PrinterOutlined />} disabled={printDisabled} onClick={onPrint} aria-label={isMobile ? text("Print") : undefined}>
                {isMobile ? null : text("Print")}
              </Button>
            </Tooltip>
          ) : null}
          {onDownload ? (
            <Tooltip title={isMobile ? text("Download") : undefined}>
              <Button type={downloadPrimary ? "primary" : "default"} icon={<DownloadOutlined />} disabled={downloadDisabled} onClick={onDownload} aria-label={isMobile ? text("Download") : undefined}>
                {isMobile ? null : text("Download")}
              </Button>
            </Tooltip>
          ) : null}
          {onClose ? (
            <Tooltip title={isMobile ? text("Close") : undefined}>
              <Button icon={<CloseOutlined />} onClick={onClose} aria-label={isMobile ? text("Close") : undefined}>
                {isMobile ? null : text("Close")}
              </Button>
            </Tooltip>
          ) : null}
        </Flex>
      </Flex>
      {contentLoading === undefined ? content : (
        <LoadingOverlay visible={contentLoading} scope="block">{content}</LoadingOverlay>
      )}
    </div>
  );
}
